import * as ExcelJS from "exceljs";
import { Chart, ArcElement, DoughnutController, Legend, Tooltip } from "chart.js";
import { CaGroupBy, REPARTITION_CA_DATA, RepartitionCaDetail } from "../../models/data.model";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";

Chart.register(ArcElement, DoughnutController, Legend, Tooltip);

const CHART_COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#8cd17d", "#a0cbe8",
];

const C = {
  headerBg: "FF1B2A4A", headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3", groupFont: "FF1B2A4A",
  subBg: "FFF5F7FA", border: "FFD0D5DD",
  statGreen: "FFE6FFF5", statPurple: "FFF0E6FF", statOrange: "FFFFF0E0",
  titleColor: "FF1B2A4A",
  margeGood: "FF16A34A", margeWarn: "FFF59E0B", margeBad: "FFDC2626",
};

function colLetter(col: number): string {
  let s = "", c = col;
  while (c > 0) { c--; s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26); }
  return s;
}
function cellRef(col: number, row: number): string { return `${colLetter(col)}${row}`; }

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}
function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

/** Double-ring doughnut: outer = CA, inner = Marge */
function renderDoubleRingPng(labels: string[], caValues: number[], margeValues: number[]): string {
  const canvas = document.createElement("canvas");
  canvas.width = 500; canvas.height = 500;
  const ctx = canvas.getContext("2d")!;
  const colors = CHART_COLORS.slice(0, labels.length);
  const chart = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels,
      datasets: [
        { label: "CA", data: caValues, backgroundColor: colors, borderWidth: 1, borderColor: "#fff" },
        { label: "Marge", data: margeValues, backgroundColor: colors.map((c) => c + "99"), borderWidth: 1, borderColor: "#fff" },
      ],
    },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: true, position: "bottom", labels: { font: { size: 10 }, padding: 6 } },
        tooltip: { enabled: false },
      },
    },
  });
  const dataUrl = canvas.toDataURL("image/png");
  chart.destroy();
  return dataUrl.replace(/^data:image\/png;base64,/, "");
}

// ─────────────────────────────────────────────────────────────────────────────

const groupFieldMap: Record<CaGroupBy, keyof RepartitionCaDetail> = {
  byClient: "tiersDenomination", byArticle: "articleDenomination",
  byFamille: "familleDenomination", byGroupeTiers: "tiersGroupe",
};
const subFieldMap: Record<CaGroupBy, keyof RepartitionCaDetail> = {
  byClient: "articleDenomination", byArticle: "tiersDenomination",
  byFamille: "articleDenomination", byGroupeTiers: "tiersDenomination",
};

// Columns: Groupe | Détail | CA | Coût Matière | Coût MO | Coût Total | Marge | Taux | % CA
const COL = { grp: 1, sub: 2, ca: 3, mat: 4, mo: 5, cout: 6, marge: 7, taux: 8, pct: 9 };
const HEADER_ROW = 5;

function buildSheet(
  wb: ExcelJS.Workbook, sheetName: string, groupByKey: CaGroupBy,
  items: { denomination: string; total: number }[],
  details: RepartitionCaDetail[], grandTotal: number,
  opts: { includeMO: boolean; includeRemisePied: boolean },
) {
  const montantOf = (d: RepartitionCaDetail) =>
    opts.includeRemisePied ? d.montantHT : (d.montantBrut ?? d.montantHT);
  const coutOf = (d: RepartitionCaDetail) =>
    (d.coutMateriel ?? 0) + (opts.includeMO ? (d.coutMO ?? 0) : 0);

  const ws = wb.addWorksheet(sheetName, { views: [{ state: "frozen", ySplit: HEADER_ROW }] });
  const gField = groupFieldMap[groupByKey];
  const sField = subFieldMap[groupByKey];

  // Précalcul du CA agrégé par groupe en appliquant les toggles. Sert au tri
  // ET au tableau (sinon le back trie selon `total` calculé avec toggles par
  // défaut, ce qui peut diverger de l'ordre attendu).
  const adjustedTotalMap = new Map<string, number>();
  for (const d of details) {
    const key = String(d[gField]);
    adjustedTotalMap.set(key, (adjustedTotalMap.get(key) ?? 0) + montantOf(d));
  }
  // Pas de filtre `total > 0` : afficher TOUS les groupes (positifs et négatifs)
  // pour que la ligne TOTAL GÉNÉRAL coïncide avec le bloc Stats (somme nette
  // = CA - avoirs). Sans ça les % CA divergent entre dialog et Excel.
  const sorted = [...items].sort(
    (a, b) =>
      (adjustedTotalMap.get(b.denomination) ?? 0) -
      (adjustedTotalMap.get(a.denomination) ?? 0),
  );
  // Niveau article intercalé seulement pour byGroupeTiers — les autres modes
  // ont déjà l'article au niveau group ou sub, pas besoin de redondance.
  const includeArticleLevel = groupByKey === "byGroupeTiers";

  [28, 28, 14, 14, 14, 14, 14, 10, 10].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // Title (laisse la dernière colonne libre pour le branding JustUp)
  ws.mergeCells("A1:H1");
  const tc = ws.getCell("A1");
  tc.value = `Répartition CA — ${sheetName}`;
  tc.font = { bold: true, size: 16, color: { argb: C.titleColor } };
  ws.getRow(1).height = 30;

  // Stats — alignés sur les toggles MO/remise pied
  const totalMat = details.reduce((s, d) => s + (d.coutMateriel ?? 0), 0);
  const totalMO = opts.includeMO
    ? details.reduce((s, d) => s + (d.coutMO ?? 0), 0)
    : 0;
  const totalCout = totalMat + totalMO;
  const totalCa = details.reduce((s, d) => s + montantOf(d), 0);
  const totalMarge = totalCa - totalCout;
  const tauxMarge = totalCa !== 0 ? (totalMarge / totalCa) * 100 : 0;

  ws.mergeCells("A3:C3"); ws.mergeCells("D3:F3"); ws.mergeCells("G3:I3");
  const s1 = ws.getCell("A3");
  s1.value = `CA : ${fmt(totalCa)}`;
  statStyle(s1, C.statGreen);
  const s2 = ws.getCell("D3");
  s2.value = opts.includeMO
    ? `Mat. ${fmt(totalMat)} + MO ${fmt(totalMO)}`
    : `Mat. ${fmt(totalMat)} (MO exclue)`;
  statStyle(s2, C.statOrange);
  const s3 = ws.getCell("G3");
  s3.value = `Marge : ${fmt(totalMarge)} (${tauxMarge.toFixed(1)}%)`;
  statStyle(s3, tauxMarge >= 35 ? C.statGreen : tauxMarge >= 20 ? C.statOrange : "FFFEE2E2");
  ws.getRow(3).height = 28;

  // Headers
  const headers = [sheetName.slice(0, -1), "Détail", "CA (€)", "Matière (€)", "MO (€)", "Coût total (€)", "Marge (€)", "Taux", "% CA"];
  headers.forEach((h, i) => { const c = ws.getCell(HEADER_ROW, i + 1); c.value = h; headerStyle(c); });
  ws.getRow(HEADER_ROW).height = 24;
  ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: 9 } };

  let row = HEADER_ROW + 1;
  const groupRowNumbers: number[] = [];
  const eurFmt = '#,##0.00\\ "€"';

  for (const group of sorted) {
    const groupRowNum = row;
    groupRowNumbers.push(groupRowNum);
    row++;

    const subMap = new Map<string, { ca: number; mat: number; mo: number }>();
    // Pour le niveau article intercalé : map[sub] → map[article] → totaux.
    const subArticleMap = new Map<string, Map<string, { ca: number; mat: number; mo: number }>>();
    let groupCa = 0;
    for (const d of details) {
      if (String(d[gField]) !== group.denomination) continue;
      const sk = String(d[sField]);
      if (!subMap.has(sk)) subMap.set(sk, { ca: 0, mat: 0, mo: 0 });
      const e = subMap.get(sk)!;
      const mt = montantOf(d);
      const mat = d.coutMateriel ?? 0;
      const mo = opts.includeMO ? (d.coutMO ?? 0) : 0;
      e.ca += mt;
      e.mat += mat;
      e.mo += mo;
      groupCa += mt;
      if (includeArticleLevel) {
        const aKey = String(d.articleDenomination);
        if (!subArticleMap.has(sk)) subArticleMap.set(sk, new Map());
        const aMap = subArticleMap.get(sk)!;
        if (!aMap.has(aKey)) aMap.set(aKey, { ca: 0, mat: 0, mo: 0 });
        const ae = aMap.get(aKey)!;
        ae.ca += mt;
        ae.mat += mat;
        ae.mo += mo;
      }
    }

    const subs = [...subMap.entries()].map(([name, v]) => ({ name, ...v })).sort((a, b) => b.ca - a.ca);
    const subCaCells: string[] = [], subMatCells: string[] = [], subMoCells: string[] = [];

    for (const sub of subs) {
      const sr = ws.getRow(row);
      sr.getCell(COL.sub).value = sub.name;
      sr.getCell(COL.ca).value = sub.ca;
      sr.getCell(COL.mat).value = sub.mat;
      sr.getCell(COL.mo).value = sub.mo;
      sr.getCell(COL.cout).value = { formula: `${cellRef(COL.mat, row)}+${cellRef(COL.mo, row)}`, result: sub.mat + sub.mo } as any;
      sr.getCell(COL.marge).value = { formula: `${cellRef(COL.ca, row)}-${cellRef(COL.cout, row)}`, result: sub.ca - sub.mat - sub.mo } as any;
      sr.getCell(COL.taux).value = { formula: `IF(${cellRef(COL.ca, row)}=0,0,${cellRef(COL.marge, row)}/${cellRef(COL.ca, row)})`, result: 0 } as any;

      for (let c = 1; c <= 9; c++) {
        sr.getCell(c).font = { size: 9, italic: true };
        sr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.subBg } };
        sr.getCell(c).border = { bottom: { style: "hair", color: { argb: C.border } } };
      }
      [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach((c) => (sr.getCell(c).numFmt = eurFmt));
      sr.getCell(COL.taux).numFmt = "0.0%";

      subCaCells.push(cellRef(COL.ca, row));
      subMatCells.push(cellRef(COL.mat, row));
      subMoCells.push(cellRef(COL.mo, row));
      row++;

      // Lignes article intercalées sous le tiers — uniquement pour la feuille
      // Groupes. Préfixe "↳ " + indentation pour distinguer du niveau tiers.
      if (includeArticleLevel) {
        const aMap = subArticleMap.get(sub.name);
        if (aMap) {
          const articles = [...aMap.entries()]
            .map(([name, v]) => ({ name, ...v }))
            .sort((a, b) => b.ca - a.ca);
          for (const article of articles) {
            const ar = ws.getRow(row);
            ar.getCell(COL.sub).value = `        ↳ ${article.name}`;
            ar.getCell(COL.ca).value = article.ca;
            ar.getCell(COL.mat).value = article.mat;
            ar.getCell(COL.mo).value = article.mo;
            ar.getCell(COL.cout).value = {
              formula: `${cellRef(COL.mat, row)}+${cellRef(COL.mo, row)}`,
              result: article.mat + article.mo,
            } as any;
            ar.getCell(COL.marge).value = {
              formula: `${cellRef(COL.ca, row)}-${cellRef(COL.cout, row)}`,
              result: article.ca - article.mat - article.mo,
            } as any;
            ar.getCell(COL.taux).value = {
              formula: `IF(${cellRef(COL.ca, row)}=0,0,${cellRef(COL.marge, row)}/${cellRef(COL.ca, row)})`,
              result: 0,
            } as any;

            for (let c = 1; c <= 9; c++) {
              ar.getCell(c).font = { size: 8, color: { argb: "FF6B7280" } };
              ar.getCell(c).border = {
                bottom: { style: "hair", color: { argb: C.border } },
              };
            }
            [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach(
              (c) => (ar.getCell(c).numFmt = eurFmt),
            );
            ar.getCell(COL.taux).numFmt = "0.0%";
            row++;
          }
        }
      }
    }

    // Group row — `groupCa` est recalculé depuis les détails avec les toggles
    // appliqués (pas `group.total` qui vient du back avec toggles par défaut).
    const gr = ws.getRow(groupRowNum);
    gr.getCell(COL.grp).value = group.denomination;
    gr.getCell(COL.ca).value = subCaCells.length > 0 ? { formula: `SUM(${subCaCells.join(",")})`, result: groupCa } as any : groupCa;
    gr.getCell(COL.mat).value = subMatCells.length > 0 ? { formula: `SUM(${subMatCells.join(",")})`, result: 0 } as any : 0;
    gr.getCell(COL.mo).value = subMoCells.length > 0 ? { formula: `SUM(${subMoCells.join(",")})`, result: 0 } as any : 0;
    gr.getCell(COL.cout).value = { formula: `${cellRef(COL.mat, groupRowNum)}+${cellRef(COL.mo, groupRowNum)}`, result: 0 } as any;
    gr.getCell(COL.marge).value = { formula: `${cellRef(COL.ca, groupRowNum)}-${cellRef(COL.cout, groupRowNum)}`, result: 0 } as any;
    gr.getCell(COL.taux).value = { formula: `IF(${cellRef(COL.ca, groupRowNum)}=0,0,${cellRef(COL.marge, groupRowNum)}/${cellRef(COL.ca, groupRowNum)})`, result: 0 } as any;

    for (let c = 1; c <= 9; c++) {
      gr.getCell(c).font = { bold: true, size: 10, color: { argb: C.groupFont } };
      gr.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
      gr.getCell(c).border = { bottom: { style: "thin", color: { argb: C.border } }, top: { style: "thin", color: { argb: C.border } } };
    }
    [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach((c) => (gr.getCell(c).numFmt = eurFmt));
    gr.getCell(COL.taux).numFmt = "0.0%";
  }

  // Grand total
  row++;
  const gtRow = row;
  ws.mergeCells(gtRow, COL.grp, gtRow, COL.sub);
  const gtLabel = ws.getCell(gtRow, COL.grp);
  gtLabel.value = "TOTAL GÉNÉRAL";
  gtLabel.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtLabel.alignment = { horizontal: "right", vertical: "middle" };

  const sumRefs = (col: number) => groupRowNumbers.map((r) => cellRef(col, r)).join(",");
  ws.getCell(gtRow, COL.ca).value = { formula: `SUM(${sumRefs(COL.ca)})`, result: totalCa } as any;
  ws.getCell(gtRow, COL.mat).value = { formula: `SUM(${sumRefs(COL.mat)})`, result: totalMat } as any;
  ws.getCell(gtRow, COL.mo).value = { formula: `SUM(${sumRefs(COL.mo)})`, result: totalMO } as any;
  ws.getCell(gtRow, COL.cout).value = { formula: `${cellRef(COL.mat, gtRow)}+${cellRef(COL.mo, gtRow)}`, result: totalCout } as any;
  ws.getCell(gtRow, COL.marge).value = { formula: `${cellRef(COL.ca, gtRow)}-${cellRef(COL.cout, gtRow)}`, result: totalMarge } as any;
  ws.getCell(gtRow, COL.taux).value = { formula: `IF(${cellRef(COL.ca, gtRow)}=0,0,${cellRef(COL.marge, gtRow)}/${cellRef(COL.ca, gtRow)})`, result: 0 } as any;
  ws.getCell(gtRow, COL.pct).value = 1;

  for (let c = COL.ca; c <= COL.pct; c++) {
    ws.getCell(gtRow, c).font = { bold: true, size: 11, color: { argb: C.headerFont } };
    ws.getCell(gtRow, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  }
  [COL.ca, COL.mat, COL.mo, COL.cout, COL.marge].forEach((c) => (ws.getCell(gtRow, c).numFmt = eurFmt));
  ws.getCell(gtRow, COL.taux).numFmt = "0.0%";
  ws.getCell(gtRow, COL.pct).numFmt = "0.0%";
  ws.getRow(gtRow).height = 26;

  const gtCaRef = cellRef(COL.ca, gtRow);

  // % CA on group rows
  for (const r of groupRowNumbers) {
    ws.getCell(r, COL.pct).value = { formula: `IF(${gtCaRef}=0,0,${cellRef(COL.ca, r)}/${gtCaRef})`, result: 0 } as any;
    ws.getCell(r, COL.pct).numFmt = "0.0%";
    ws.getCell(r, COL.pct).font = { bold: true, size: 10, color: { argb: C.groupFont } };
    ws.getCell(r, COL.pct).fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
  }

  // Conditional formatting on Taux
  ws.addConditionalFormatting({
    ref: `${colLetter(COL.taux)}${HEADER_ROW + 1}:${colLetter(COL.taux)}${row}`,
    rules: [
      { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.349"], priority: 1, style: { font: { color: { argb: C.margeGood }, bold: true } } },
      { type: "cellIs", operator: "greaterThan" as any, formulae: ["0.199"], priority: 2, style: { font: { color: { argb: C.margeWarn }, bold: true } } },
      { type: "cellIs", operator: "lessThan" as any, formulae: ["0.20"], priority: 3, style: { font: { color: { argb: C.margeBad }, bold: true } } },
    ],
  });

  // Double-ring chart (col K-M)
  const chartCol = 11;
  [sheetName.slice(0, -1), "CA (€)", "Marge (€)"].forEach((h, i) => { const c = ws.getCell(HEADER_ROW, chartCol + i); c.value = h; headerStyle(c); });
  ws.getColumn(chartCol).width = 22;
  ws.getColumn(chartCol + 1).width = 14;
  ws.getColumn(chartCol + 2).width = 14;

  // Donut chart : on n'embarque que les groupes au CA positif (sinon
  // Chart.js ne rend rien). Recalcul du CA par groupe avec les toggles
  // appliqués pour rester cohérent avec le tableau.
  const groupCaMap = new Map<string, number>();
  const groupMargeMap = new Map<string, number>();
  for (const d of details) {
    const key = String(d[gField]);
    const mt = montantOf(d);
    const mo = opts.includeMO ? (d.coutMO ?? 0) : 0;
    groupCaMap.set(key, (groupCaMap.get(key) ?? 0) + mt);
    groupMargeMap.set(key, (groupMargeMap.get(key) ?? 0) + mt - (d.coutMateriel ?? 0) - mo);
  }

  const sortedForChart = sorted.filter((c) => (groupCaMap.get(c.denomination) ?? 0) > 0);
  const top12 = sortedForChart.slice(0, 12);
  top12.forEach((c, i) => {
    const r = HEADER_ROW + 1 + i;
    const grpRow = groupRowNumbers[sorted.indexOf(c)];
    const groupCaVal = groupCaMap.get(c.denomination) ?? 0;
    ws.getCell(r, chartCol).value = c.denomination; ws.getCell(r, chartCol).font = { size: 9 };
    ws.getCell(r, chartCol + 1).value = { formula: cellRef(COL.ca, grpRow), result: groupCaVal } as any;
    ws.getCell(r, chartCol + 1).numFmt = eurFmt; ws.getCell(r, chartCol + 1).font = { size: 9 };
    ws.getCell(r, chartCol + 2).value = { formula: cellRef(COL.marge, grpRow), result: 0 } as any;
    ws.getCell(r, chartCol + 2).numFmt = eurFmt; ws.getCell(r, chartCol + 2).font = { size: 9 };
  });

  const chartLabels = top12.map((c) => c.denomination);
  const chartCaValues = top12.map((c) => groupCaMap.get(c.denomination) ?? 0);
  const chartMargeValues = top12.map((c) => Math.max(0, groupMargeMap.get(c.denomination) ?? 0));
  const rest = sortedForChart.slice(12);
  if (rest.length > 0) {
    chartLabels.push("Autres");
    chartCaValues.push(rest.reduce((s, c) => s + (groupCaMap.get(c.denomination) ?? 0), 0));
    chartMargeValues.push(Math.max(0, rest.reduce((s, c) => s + (groupMargeMap.get(c.denomination) ?? 0), 0)));
  }

  if (chartLabels.length > 0) {
    try {
      const png = renderDoubleRingPng(chartLabels, chartCaValues, chartMargeValues);
      const imgId = wb.addImage({ base64: png, extension: "png" });
      ws.addImage(imgId, { tl: { col: chartCol - 1, row: HEADER_ROW + 1 + chartLabels.length + 1 } as any, ext: { width: 450, height: 450 } });
    } catch { /* skip */ }
  }
}

function fmt(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

export async function exportRepartitionCaExcel(
  data: REPARTITION_CA_DATA,
  options?: { includeMO?: boolean; includeRemisePied?: boolean },
): Promise<void> {
  const opts = {
    includeMO: options?.includeMO ?? true,
    includeRemisePied: options?.includeRemisePied ?? true,
  };
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const sheets: { key: CaGroupBy; label: string; items: { denomination: string; total: number }[] }[] = [
    { key: "byClient", label: "Clients", items: data.byClient },
    { key: "byArticle", label: "Articles", items: data.byArticle },
    { key: "byFamille", label: "Familles", items: data.byFamille },
    { key: "byGroupeTiers", label: "Groupes", items: data.byGroupeTiers },
  ];

  for (const sheet of sheets) {
    buildSheet(wb, sheet.label, sheet.key, sheet.items, data.details, data.total, opts);
    await addJustupBranding(wb, wb.getWorksheet(sheet.label)!, 9);
  }

  addRawDataSheet(wb, data.details);

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const filename = `repartition-ca-marge-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 10000);
}

function fmtDateFR(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function rawHeaderStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
}

function addRawDataSheet(wb: ExcelJS.Workbook, details: RepartitionCaDetail[]): void {
  const RAW_HEADERS = ["Client", "Groupe", "Article", "Famille", "N° Lot", "Date", "Quantité", "Unité", "Prix unit.", "Montant HT", "Coût matière", "Coût MO"];
  const RAW_WIDTHS = [28, 20, 28, 20, 16, 14, 12, 10, 14, 14, 14, 14];

  const ws = wb.addWorksheet("Données brutes", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: true }],
  });

  RAW_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  RAW_HEADERS.forEach((h, i) => {
    const c = ws.getCell(1, i + 1);
    c.value = h;
    rawHeaderStyle(c);
  });
  ws.getRow(1).height = 22;
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: RAW_HEADERS.length } };

  const eurFmt = '#,##0.00\\ "€"';
  let r = 1;
  for (const d of details) {
    r++;
    const row = ws.getRow(r);
    row.getCell(1).value = d.tiersDenomination;
    row.getCell(2).value = d.tiersGroupe;
    row.getCell(3).value = d.articleDenomination;
    row.getCell(4).value = d.familleDenomination;
    row.getCell(5).value = d.lotNumero;
    row.getCell(6).value = fmtDateFR(d.date);
    row.getCell(7).value = d.quantite;
    row.getCell(7).numFmt = "#,##0.00";
    row.getCell(8).value = d.uniteVente;
    row.getCell(9).value = d.prixUnitaire;
    row.getCell(9).numFmt = eurFmt;
    row.getCell(10).value = d.montantHT;
    row.getCell(10).numFmt = eurFmt;
    row.getCell(11).value = d.coutMateriel;
    row.getCell(11).numFmt = eurFmt;
    row.getCell(12).value = d.coutMO;
    row.getCell(12).numFmt = eurFmt;
    for (let c = 9; c <= 12; c++) row.getCell(c).alignment = { horizontal: "right" };
  }
}
